import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiSearch, FiFilter, FiMoreVertical, FiRefreshCcw, FiCheckCircle, FiPieChart, FiArrowRight, FiShield, FiLoader, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { formatCurrency } from '../../utils/formatters';
import { CREDIT_STATUSES } from '../../data/vendorCreditConstants';
import ApplyCreditModal from '../../components/vendors/ApplyCreditModal';
import VendorCreditForm from './VendorCreditForm';
import { vendorCreditService } from '../../services/vendorCreditService';

export default function VendorCredits() {
  const [credits, setCredits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals state
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = await vendorCreditService.getCredits();
      setCredits(data || []);
    } catch (error) {
      console.error("Failed to fetch credits:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenApply = (credit) => {
    setSelectedCredit(credit);
    setIsApplyModalOpen(true);
  };

  const handleOpenForm = (credit = null) => {
    setSelectedCredit(credit);
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this credit memo?")) {
      try {
        await vendorCreditService.deleteCredit(id);
        fetchData();
      } catch (error) {
        console.error("Failed to delete credit:", error);
      }
    }
  };

  // Dynamic Calculations
  const filteredCredits = credits.filter(c => 
    c.vendor?.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    c.credit_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalAvailable = credits
    .filter(c => c.status === 'AVAILABLE' || c.status === 'PARTIAL')
    .reduce((sum, c) => sum + Number(c.balance), 0);

  const totalApplied = credits
    .reduce((sum, c) => sum + (Number(c.total_amount) - Number(c.balance)), 0);

  const vendorsWithCredits = new Set(
    credits.filter(c => Number(c.balance) > 0).map(c => c.vendor_id)
  ).size;

  return (
    <div className="max-w-[1400px] mx-auto pb-12 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Vendor Credits</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium italic">Manage purchase returns and billing corrections.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => handleOpenForm()}
            className="bg-slate-900 hover:bg-black text-white px-8 py-3 rounded-full text-sm font-bold transition-all shadow-xl flex items-center gap-2 active:scale-95"
          >
            <SafeIcon icon={FiPlus} /> Create Credit Memo
          </button>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 text-emerald-600 mb-2">
            <SafeIcon icon={FiCheckCircle} className="text-xl" />
            <span className="text-[10px] font-black uppercase tracking-widest">Total Available</span>
          </div>
          <div className="text-3xl font-black text-slate-900">{formatCurrency(totalAvailable)}</div>
          <p className="text-xs text-slate-400 mt-2 font-bold">Ready to apply to bills</p>
        </div>
        
        <div className="bg-white p-8 rounded-[40px] border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 text-blue-600 mb-2">
            <SafeIcon icon={FiPieChart} className="text-xl" />
            <span className="text-[10px] font-black uppercase tracking-widest">Applied Historically</span>
          </div>
          <div className="text-3xl font-black text-slate-900">{formatCurrency(totalApplied)}</div>
          <p className="text-xs text-slate-400 mt-2 font-bold">Reduced total liabilities</p>
        </div>
        
        <div className="bg-blue-600 p-8 rounded-[40px] text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-2">AP Optimization</h3>
            <p className="text-lg font-bold leading-snug">
              You have {vendorsWithCredits} vendor{vendorsWithCredits !== 1 ? 's' : ''} with unapplied credits.
            </p>
            <button className="mt-4 flex items-center gap-2 text-xs font-black uppercase tracking-widest hover:underline">
              Auto-Apply All <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
          <SafeIcon icon={FiShield} className="absolute -right-4 -bottom-4 text-8xl text-white/10" />
        </div>
      </div>

      {/* Main Table Area */}
      <div className="bg-white border border-slate-200 rounded-[40px] shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-sm">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search by vendor or credit #..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-[13px] font-bold outline-none focus:ring-2 focus:ring-blue-500/20" 
            />
          </div>
          <div className="flex gap-2">
            <button className="p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-slate-600 transition-all"><SafeIcon icon={FiFilter} /></button>
            <button onClick={fetchData} className="p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-slate-600 transition-all"><SafeIcon icon={FiRefreshCcw} /></button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <SafeIcon icon={FiLoader} className="text-4xl animate-spin mb-4 text-blue-600" />
            <p className="text-sm font-bold uppercase tracking-widest">Loading Credits...</p>
          </div>
        ) : (
          <div className="overflow-x-auto min-h-[300px]">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-200">
                <tr>
                  <th className="px-8 py-5">Status</th>
                  <th className="px-8 py-5">Credit No.</th>
                  <th className="px-8 py-5">Vendor</th>
                  <th className="px-8 py-5">Reason</th>
                  <th className="px-8 py-5 text-right">Available Balance</th>
                  <th className="px-8 py-5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="text-[13px]">
                {filteredCredits.length === 0 ? (
                   <tr>
                     <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                       No vendor credits found.
                     </td>
                   </tr>
                ) : (
                  filteredCredits.map(credit => (
                    <tr key={credit.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors group">
                      <td className="px-8 py-5">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${CREDIT_STATUSES[credit.status]?.color || CREDIT_STATUSES.AVAILABLE.color}`}>
                          {CREDIT_STATUSES[credit.status]?.label || credit.status}
                        </span>
                      </td>
                      <td className="px-8 py-5 font-black text-slate-900">{credit.credit_number}</td>
                      <td className="px-8 py-5 font-bold text-blue-600 hover:underline cursor-pointer">{credit.vendor?.name || 'Unknown'}</td>
                      <td className="px-8 py-5">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px] font-black uppercase">
                          {credit.reason || 'Other'}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <p className="font-black text-slate-900">{formatCurrency(credit.balance)}</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">Original: {formatCurrency(credit.total_amount)}</p>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex items-center justify-center gap-2">
                          {Number(credit.balance) > 0 && (
                            <button 
                              onClick={() => handleOpenApply(credit)}
                              className="px-4 py-1.5 bg-blue-600 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-md active:scale-95"
                            >
                              Apply to Bill
                            </button>
                          )}
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleOpenForm(credit)} className="p-1.5 text-slate-400 hover:text-blue-600"><SafeIcon icon={FiEdit2} /></button>
                            <button onClick={() => handleDelete(credit.id)} className="p-1.5 text-slate-400 hover:text-rose-600"><SafeIcon icon={FiTrash2} /></button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <ApplyCreditModal 
        isOpen={isApplyModalOpen} 
        onClose={() => setIsApplyModalOpen(false)} 
        credit={selectedCredit}
        openBills={[
          // Mock data for the modal to demonstrate UI without building full bill application logic
          { id: 'b1', number: 'BILL-10042', dueDate: '2026-08-14', balance: 1250.50 },
          { id: 'b2', number: 'BILL-10041', dueDate: '2026-07-24', balance: 8400.00 }
        ]}
      />

      <VendorCreditForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        initialData={selectedCredit}
        onSuccess={fetchData}
      />
    </div>
  );
}